import url from 'url';
import { CookieName, ProductType } from '@/shared/enums';
import store from '@/shared/store';
import DeviceInfo from '@/shared/libs/device-info';
import Cookie from 'js-cookie';
import NetworkInfo from '@/shared/libs/network-info';

export const getCategory = (product) => {
  const { mainCategory } = product.currentProgram || product;

  if (mainCategory) {
    return mainCategory.key;
  }
};

export const getTitle = ({ currentProgram, title, originalTitle, type } = {}) => {
  if (type === ProductType.LIVE && currentProgram) {
    return currentProgram.originalTitle || currentProgram.title;
  }

  return originalTitle || title;
};

export const getChannelTitle = (product, liveManager) => {
  if (product.type === ProductType.LIVE) {
    return product.title;
  }

  if (!product.channelId) {
    return;
  }

  const channel = liveManager.channels.get(product.channelId);
  return channel?.title;
};

export const getDeviceParams = () => {
  return {
    param_platform: store.state.platform.name,
    param_device_os: DeviceInfo.getSystemName(),
    param_device_os_version: DeviceInfo.getSystemVersion(),
    param_browser: DeviceInfo.getBrowserName(),
    param_browser_version: DeviceInfo.getBrowserVersion(),
    param_session_id: Cookie.get(CookieName.DEVICE_UID),
  };
};

export const getDuration = (product) => {
  if (product.type === ProductType.PROGRAMME) {
    return calcDuration(product);
  }

  if (product.type === ProductType.LIVE && product.currentProgram) {
    return calcDuration(product.currentProgram);
  }

  return product.duration;
};

const calcDuration = ({ since, till }) => {
  if (!since || !till) {
    return;
  }

  return Math.floor((till - since) / 1000); // ms to s
};

export const getProfileType = (profile) => {
  return profile.kids ? 'Child' : 'Main';
};

export const getServiceParams = () => {
  return {
    param_service: 'aVOD',
    param_app_version: `${window.appInfo.buildDate} (${window.appInfo.version})`,
  };
};

export const getTechnicalParams = (product, bitrateMonitor) => {
  const video = document.querySelector('#player video');
  const freewheelVideo = document.querySelector('.js-freewheel-video');

  let resolution;
  let bitrate;

  if (freewheelVideo && freewheelVideo.videoWidth > 0 && freewheelVideo.videoHeight > 0) {
    resolution = `${freewheelVideo.videoWidth}x${freewheelVideo.videoHeight}`;
  } else if (video && video.videoWidth > 0 && video.videoHeight > 0) {
    bitrate = bitrateMonitor.getAll();

    resolution = `${video.videoWidth}x${video.videoHeight}`;
  }

  const networkType = NetworkInfo.getNetworkType();
  const signalLevel = NetworkInfo.getSignalLevel() || '';

  return {
    param_network_type: capitalizeWord(networkType),
    param_signal_level: signalLevel.toUpperCase(),
    param_content_url: product.shareUrl || location.href,
    param_video_bitrate: bitrate?.current,
    param_video_resolution: resolution,
    param_min_bitrate: bitrate?.min,
    param_max_bitrate: bitrate?.max,
  };
};

export const getSeasonAndEpisode = (product) => {
  if (!product || product.type !== ProductType.EPISODE) {
    return '';
  }

  const season = String(product.season.number).padStart(2, '0');
  const episode = String(product.episode).padStart(2, '0');
  return `S${season}E${episode}`;
};

export const getEpisodeTitle = (product) => {
  if (!product || product.type !== ProductType.EPISODE) {
    return '';
  }

  return product.originalTitle || product.title;
};

export const getType = ({ type, isCurrent } = {}) => {
  if (type === ProductType.PROGRAMME && isCurrent) {
    return ProductType.LIVE;
  }

  return type ? type.toUpperCase() : '';
};

export const getLocation = () => {
  let location = document.location;

  if (document.referrer) {
    location = url.parse(document.referrer);
  }

  return location;
};

export const mapByFieldName = (array, fieldName) => {
  if (!Array.isArray(array) || !array.length || !fieldName) {
    return;
  }

  return array.map((key) => key[fieldName]);
};

export const normalizeQuality = (quality = '') => {
  return String(quality).split(',')[1]?.trim() || quality;
};

export const removeEmptyParams = (params) => {
  Object.keys(params).forEach((key) => {
    if (params[key] === undefined || params[key] === '') {
      delete params[key];
    }
  });

  return params;
};

const capitalizeWord = (word = '') => {
  return word.charAt(0).toUpperCase() + word.substring(1).toLowerCase();
};

export const getModelForStats = (product) => {
  if (product.type === ProductType.LIVE && product.currentProgram && Object.keys(product.currentProgram).length) {
    return product.currentProgram;
  }

  return product;
};

export const getLanguageIso6392 = (currentLanguage) => {
  const language = (currentLanguage || '').toUpperCase();
  const mapping = {
    EN: 'ENG',
    LV: 'LAV',
    LT: 'LIT',
    ET: 'EST',
    RU: 'RUS',
  };

  return mapping[language] || language;
};
